<template>
    <div
        class="enum-filter"
        :data-test-id="`${field}-filter-field`"
    >
        <div class="enum-filter__option">
            <Checkbox
                binary
                :indeterminate="isPartiallySelected"
                :inputId="anyInputId"
                :modelValue="isAllSelected"
                :pt="makeTestIdPassThrough(`${field}-filter-option-any`)"
                @update:modelValue="toggleAll"
            />
            <label
                class="enum-filter__label"
                :for="anyInputId"
            >
                Any
            </label>
        </div>
        <div
            v-for="option in options"
            :key="option.value"
            class="enum-filter__option"
        >
            <Checkbox
                :inputId="inputIdFor(option)"
                :modelValue="selectedValues"
                :pt="makeTestIdPassThrough(`${field}-filter-option-${option.value}`)"
                :value="option.value"
                @update:modelValue="handleSelectionChange"
            />
            <label
                class="enum-filter__label"
                :for="inputIdFor(option)"
            >
                <img
                    v-if="option.icon"
                    alt=""
                    class="enum-filter__icon"
                    :data-test-id="`${field}-filter-option-icon-${option.value}`"
                    :src="option.icon"
                />
                {{ option.label }}
            </label>
        </div>
    </div>
</template>

<script>
import Checkbox from 'primevue/checkbox'

export default {
    name: 'EnumFilter',

    components: {
        Checkbox,
    },

    props: {
        field: {
            type: String,
            required: true,
        },
        modelValue: {
            type: Array,
            required: false,
            default: null,
        },
        options: {
            type: Array,
            required: true,
        },
    },

    emits: ['update:modelValue'],

    computed: {
        anyInputId() {
            return `${this.field}-filter-option-any-input`
        },
        allValues() {
            return this.options.map(({ value }) => value)
        },
        // Null means untouched, either on first load or after the filter
        // menu's Clear button. The default is everything selected, which
        // makes the options list an allow list: an option left out of it
        // can never show up in the results.
        selectedValues() {
            return this.modelValue ?? this.allValues
        },
        isAllSelected() {
            return this.options.length > 0 && this.selectedValues.length === this.options.length
        },
        isPartiallySelected() {
            return this.selectedValues.length > 0 && !this.isAllSelected
        },
    },

    methods: {
        inputIdFor(option) {
            return `${this.field}-filter-option-${option.value}-input`
        },
        handleSelectionChange(values) {
            this.$emit('update:modelValue', values)
        },
        toggleAll() {
            this.$emit('update:modelValue', this.isAllSelected ? [] : this.allValues)
        },
        makeTestIdPassThrough(dataTestId) {
            return {
                input: {
                    'data-test-id': dataTestId,
                },
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.enum-filter {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 12em;
}

.enum-filter__option {
    display: flex;
    align-items: center;
    gap: 8px;

    // Makes all of PrimeVue checkbox clickable
    :deep(.p-checkbox-input) {
        width: 100%;
        height: 100%;
    }
}

.enum-filter__label {
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
    white-space: nowrap;
}

.enum-filter__icon {
    width: 16px;
    height: 16px;
}
</style>
